import React from "react";
import BeaconTable from "./BeaconTable";
import BecConfXyTable from "./BecConfXyTable";
import BothFloorsNeighborsTable from "./BothFloorsNeighborsTable";
import "./ImageMapper.css";

const BeaconMapperView = ({
  markers,
  connections,
  imageSrc,
  imageDimensions,
  imgRef,
  imageContainerRef,
  maxNeighborsToConnect,
  activeMarkerId,
  markerIdForRemovalConfirmation,
  connectionIdForRemovalConfirmation,
  dragStartMarker,
  currentDragLineEnd,
  isDraggingLine,
  selectedFloorId,
  showClearAllConfirmation,
  beaconTypes,
  getBeaconTableData,
  getMarkerDisplayPosition,
  getBeaconTypeOptions,
  handleClearAllClick,
  setShowClearAllConfirmation,
  clearAllBeacons,
  handleImageUpload,
  handleConnectAllNeighbors,
  handleClearAllConnections,
  handleImageClick,
  handleImageContainerMouseMove,
  handleImageContainerMouseUp,
  handleDownloadImage,
  handleAddRow,
  handleUpdateMarker,
  handleDeleteMarker,
  handleUpdateConnection,
  handleAddConnectionRow,
  handleDeleteConnection,
  handleMarkerMouseDown,
  handleMarkerMouseUp,
  handleMarkerSingleClick,
  handleBeaconTypeChange,
  confirmRemoveMarker,
  cancelRemoveMarker,
  confirmRemoveConnection,
  cancelRemoveConnection,
  handleConnectionLineClick,
  setMaxNeighborsToConnect,
  pointInputMarkerId,
  handlePointValueSubmit,
  availableBeaconsData,
  showBeaconNameDropdownForMarkerId, 
  handleBeaconNameChange, 
}) => {
  return (
    <div className="beacon-mapper-container">
      <div className="beacon-mapper-header1">
        <h2 className="beacon-mapper-title">Beacon Mapper</h2>
        <div className="beacon-mapper-controls">
          {markers.length > 0 && (
            <button
              onClick={handleClearAllClick}
              className="clear-all-btn"
              title="Remove all beacons"
            >
              Clear All Beacons
            </button>
          )}
        </div>
      </div>

      {showClearAllConfirmation && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 className="modal-title">Confirm Clear All</h3>
            <p className="modal-message">
              Are you sure you want to remove all beacons and connections? This
              action cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                onClick={() => setShowClearAllConfirmation(false)}
                className="modal-cancel-btn"
              >
                Cancel
              </button>
              <button onClick={clearAllBeacons} className="modal-clear-btn">
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="beacon-mapper-toolbar">
        <input
          type="file"
          accept="image/png, image/jpeg, image/gif"
          onChange={handleImageUpload}
          className="file-input"
        />
        {imageSrc && markers.length > 1 && (
          <>
            <label htmlFor="max-neighbors" className="max-neighbors-label">
              Max Connections:
            </label>
            <select
              id="max-neighbors"
              value={maxNeighborsToConnect}
              onChange={(e) => setMaxNeighborsToConnect(e.target.value)}
              className="max-neighbors-select"
            >
              {[1, 2, 3, 4, 5, 6].map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>
            <button
              onClick={handleConnectAllNeighbors}
              className="connect-neighbors-btn"
            >
              Connect Neighbors
            </button>
          </>
        )}
        {imageSrc && connections.length > 0 && (
          <button
            onClick={handleClearAllConnections}
            className="clear-connections-btn"
          >
            Clear All Connections
          </button>
        )}
      </div>

      {imageSrc && (
        <div
          ref={imageContainerRef}
          className="image-mapping-area"
          style={{
            width: imageDimensions.width,
            height: imageDimensions.height,
            position: "relative",
          }}
          onClick={handleImageClick}
          onMouseMove={handleImageContainerMouseMove}
          onMouseUp={handleImageContainerMouseUp}
          onMouseLeave={handleImageContainerMouseUp}
        >
          <img
            ref={imgRef}
            src={imageSrc}
            alt="Floor Plan"
            className="floorplan-img"
            style={{ width: "100%", height: "100%", pointerEvents: "none" }}
          />
          <svg
            className="connections-svg"
            style={{
              width: imageDimensions.width,
              height: imageDimensions.height,
            }}
          >
            {connections.map((conn) => {
              const markerFrom = markers.find((m) => m.id === conn.from);
              const markerTo = markers.find((m) => m.id === conn.to);
              if (!markerFrom || !markerTo || !imgRef.current) return null;

              const naturalWidth = parseFloat(
                imgRef.current.dataset.naturalWidth || 1
              );
              const naturalHeight = parseFloat(
                imgRef.current.dataset.naturalHeight || 1
              );

              const x1 = (markerFrom.x / naturalWidth) * imageDimensions.width;
              const y1 =
                (markerFrom.y / naturalHeight) * imageDimensions.height;
              const x2 = (markerTo.x / naturalWidth) * imageDimensions.width;
              const y2 = (markerTo.y / naturalHeight) * imageDimensions.height;

              return (
                <line
                  key={conn.id}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke="rgba(255, 0, 0, 0.7)"
                  strokeWidth="4"
                  className="connection-line"
                  onClick={(e) => handleConnectionLineClick(conn.id, e)}
                />
              );
            })}
            {isDraggingLine && dragStartMarker && currentDragLineEnd && (
              <line
                x1={dragStartMarker.x}
                y1={dragStartMarker.y}
                x2={currentDragLineEnd.x}
                y2={currentDragLineEnd.y}
                stroke="rgba(0, 0, 0, 0.5)"
                strokeWidth="2"
                strokeDasharray="5,5"
              />
            )}
          </svg>

          {markers.map((marker) => {
            const { displayX, displayY, markerBgColor } =
              getMarkerDisplayPosition(marker) || {};
            if (displayX === undefined) return null;

            const isDropdownActive = activeMarkerId === marker.id;
            const isBeaconRemovalConfirmActive =
              markerIdForRemovalConfirmation === marker.id;
            const isBeaconNameDropdownActive =
              showBeaconNameDropdownForMarkerId === marker.id;

            return (
              <div
                key={marker.id}
                id={`marker-div-${marker.id}`}
                className="marker-div"
                style={{
                  left: `${displayX - 8}px`,
                  top: `${displayY - 8}px`,
                  zIndex:
                    isDropdownActive ||
                    isBeaconRemovalConfirmActive ||
                    isBeaconNameDropdownActive
                      ? 10
                      : 5,
                }}
                onMouseDown={(e) => handleMarkerMouseDown(marker, e)}
                onMouseUp={(e) => handleMarkerMouseUp(marker, e)}
                onClick={(e) => handleMarkerSingleClick(marker.id, e)}
              >
                <div className={`marker-dot ${markerBgColor}`}></div>
                <span
                  className="marker-label"
                  style={{ transform: "translate(10px, -10px)" }}
                >
                  {marker.id}
                </span>

                {isDropdownActive && (
                  <div
                    className="marker-dropdown"
                    style={{ top: "20px", left: "0px", minWidth: "120px" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label
                      htmlFor={`beacon-type-${marker.id}`}
                      className="dropdown-label"
                    >
                      Beacon Type:
                    </label>
                    <select
                      id={`beacon-type-${marker.id}`}
                      value={marker.type || ""}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleBeaconTypeChange(marker.id, e.target.value);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      className="dropdown-select"
                    >
                      <option value="" disabled>
                        Select Type
                      </option>
                      {getBeaconTypeOptions()}
                    </select>
                  </div>
                )}

                {pointInputMarkerId === marker.id && (
                  <div
                    className="marker-point-popup"
                    style={{ top: "50px", left: "0px", minWidth: "150px" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label
                      htmlFor={`point-value-${marker.id}`}
                      className="popup-label"
                    >
                      Enter Point Value:
                    </label>
                    <input
                      id={`point-value-${marker.id}`}
                      type="text"
                      onChange={(e) => (marker.tempPointValue = e.target.value)}
                      className="popup-input"
                    />
                    <button
                      onClick={() =>
                        handlePointValueSubmit(marker.id, marker.tempPointValue)
                      }
                      className="popup-submit-btn"
                    >
                      Save
                    </button>
                  </div>
                )}

                {isBeaconNameDropdownActive && (
                  <div
                    id={`beacon-name-dropdown-${marker.id}`}
                    className="marker-dropdown"
                    style={{ top: "80px", left: "0px", minWidth: "150px" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label
                      htmlFor={`beacon-name-${marker.id}`}
                      className="dropdown-label"
                    >
                      Select Beacon:
                    </label>
                    <select
                      id={`beacon-name-${marker.id}`}
                      value={marker.bec_id || ""}
                      onChange={(e) => {
                        e.stopPropagation();
                        const selectedBecId = e.target.value;
                        const selectedBeacon = availableBeaconsData.find(
                          (beacon) => String(beacon.bec_id) === selectedBecId
                        );
                        handleBeaconNameChange(
                          marker.id,
                          selectedBeacon ? selectedBeacon.bec_id : null,
                          selectedBeacon ? selectedBeacon.bec_name : ""
                        );
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      className="dropdown-select"
                    >
                      <option value="" disabled>
                        Select Beacon Name
                      </option>
                      {availableBeaconsData.map((beacon) => (
                        <option key={beacon.bec_id} value={beacon.bec_id}>
                          {beacon.bec_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {isBeaconRemovalConfirmActive && (
                  <div
                    id={`remove-confirm-popup-${marker.id}`}
                    className="marker-remove-popup"
                    style={{ top: "20px", left: "0px", minWidth: "150px" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <p className="remove-popup-text">Remove this beacon?</p>
                    <div className="remove-popup-actions">
                      <button
                        onClick={() => confirmRemoveMarker(marker.id)}
                        className="remove-btn"
                      >
                        Remove
                      </button>
                      <button
                        onClick={cancelRemoveMarker}
                        className="cancel-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {connectionIdForRemovalConfirmation &&
            (() => {
              const conn = connections.find(
                (c) => c.id === connectionIdForRemovalConfirmation
              );
              if (!conn) return null;
              const fromMarker = markers.find((m) => m.id === conn.from);
              const toMarker = markers.find((m) => m.id === conn.to);
              if (!fromMarker || !toMarker) return null;

              const pos1 = getMarkerDisplayPosition(fromMarker);
              const pos2 = getMarkerDisplayPosition(toMarker);
              if (!pos1 || !pos2) return null;

              const midX = (pos1.displayX + pos2.displayX) / 2;
              const midY = (pos1.displayY + pos2.displayY) / 2;

              return (
                <div
                  id={`remove-conn-popup-${connectionIdForRemovalConfirmation}`}
                  className="marker-remove-popup"
                  style={{
                    top: `${midY}px`,
                    left: `${midX}px`,
                    transform: "translate(-50%, -50%)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <p className="remove-popup-text">Remove this connection?</p>
                  <div className="remove-popup-actions">
                    <button
                      onClick={cancelRemoveConnection}
                      className="cancel-btn"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() =>
                        confirmRemoveConnection(
                          connectionIdForRemovalConfirmation
                        )
                      }
                      className="remove-btn"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })()}
        </div>
      )}

      {!imageSrc && (
        <div className="no-image-msg">
          <p>No image uploaded. Please select an image to begin.</p>
        </div>
      )}

      {imageSrc && (
        <div className="beacon-table-section">
          <div className="beacon-table-toolbar">
            <button
              onClick={handleDownloadImage}
              className="download-btn"
              title="Download Image with Beacons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="download-btn-icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {imageSrc && (
        <div className="beacon-table-section">
          <div className="table-scroll-wrapper">
            <BeaconTable
              markers={getBeaconTableData()}
              beaconTypes={beaconTypes}
              onAddRow={handleAddRow}
              onUpdateMarker={handleUpdateMarker}
              onDeleteMarker={handleDeleteMarker}
            />
          </div>
        </div>
      )}

      {imageSrc && (
        <div className="bec-conf-xy-table-section">
          <div className="table-scroll-wrapper">
            <BecConfXyTable markers={getBeaconTableData()} />
          </div>
        </div>
      )}

      {imageSrc && connections.length > 0 && (
        <div className="neighbors-table-section">
          <div className="table-scroll-wrapper">
            <BothFloorsNeighborsTable
              connections={connections}
              selectedFloorId={selectedFloorId}
              onUpdateConnection={handleUpdateConnection}
              onAddRow={handleAddConnectionRow}
              onDeleteConnection={handleDeleteConnection}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BeaconMapperView;
